/**
 * PlayerEntity - Player controlled by user input
 *
 * The player stores pending input from keyboard and executes it
 * during the next tick. This allows player movement to feel immediate
 * while staying within the CA system.
 */

import Phaser from "phaser";
import {
  BaseTile,
  MoveContext,
  MoveResult,
  TextureDescriptor,
} from "./BaseTile";
import { TILE, isEmpty, isArrowTile, getArrowDirection } from "../constants/gameConstants";
import { Coordinates, TileIndex } from "../types";
import { Direction } from "../utils/Direction";
import { GAME_CONFIG, COLORS } from "../config";
import { logger } from "../utils/logger";

export class PlayerEntity extends BaseTile {
  // Pending input from keyboard
  private pendingMove?: Direction;

  // Power-ups collected by player
  hasFlippers: boolean = false;
  hasSuctionBoots: boolean = false;

  // Forced arrow movement state
  private timeSinceLastMove: number = 0;
  private forcedDirection?: Direction;

  constructor({ row, col, id }: { row: number; col: number; id?: string }) {
    super(id ?? `player-${col}-${row}`, col, row);
  }

  /**
   * Grid position getter for compatibility with old PlayerState interface
   */
  get gridPos(): Coordinates {
    return this.coordinates();
  }

  /**
   * Queue a move direction from user input
   */
  queueMove(direction: Direction): void {
    this.pendingMove = direction;
  }

  /**
   * Clear any pending move
   */
  clearPendingMove(): void {
    this.pendingMove = undefined;
  }

  /**
   * Set forced movement direction (when entering arrow tile)
   */
  private setForcedDirection(direction: Direction): void {
    this.forcedDirection = direction;
    this.timeSinceLastMove = 0;
  }

  /**
   * Clear forced movement direction (when exiting arrow tile)
   */
  private clearForcedDirection(): void {
    this.forcedDirection = undefined;
    this.timeSinceLastMove = 0;
  }

  tileType(): TileIndex {
    return TILE.PLAYER_START;
  }

  textureDescriptor(): TextureDescriptor {
    return {
      type: "procedural",
      generator: (scene: Phaser.Scene, key: string) => {
        PlayerEntity.generateTexture(scene, key);
      },
    };
  }

  static generateTexture(scene: Phaser.Scene, key: string): void {
    const size = GAME_CONFIG.tileSize;
    const radius = GAME_CONFIG.playerRadius;

    const graphics = scene.add.graphics();
    graphics.fillStyle(COLORS.red, 1);
    graphics.fillCircle(size / 2, size / 2, radius);

    graphics.generateTexture(key, size, size);
    graphics.destroy();
  }

  isEntity(): boolean {
    return true; // Player is a movable entity
  }

  move(context: MoveContext): MoveResult {
    let direction: Direction;

    // FORCED ARROW MOVEMENT - takes priority over input
    if (this.forcedDirection) {
      this.timeSinceLastMove += context.deltaMs;

      if (this.timeSinceLastMove < GAME_CONFIG.arrowTileSpeed) {
        logger.debug("[DEBUG] PlayerEntity waiting on forced move timer:");
        return { type: "wait" };
      }

      this.timeSinceLastMove = 0;
      direction = this.forcedDirection;
      logger.debug("[DEBUG] PlayerEntity executing forced move:", direction);
    } else {
      // NORMAL INPUT HANDLING
      if (!this.pendingMove) {
        return { type: "wait" };
      }

      direction = this.pendingMove;
      this.clearPendingMove();
    }

    // Check what's at target
    const { tile, entity } = context.getAtDirection(
      this.coordinates(),
      direction
    );

    /*     logger.debug("[DEBUG] PlayerEntity checking move to:", {
          targetTile: tile,
          targetEntity: entity,
          direction,
        });
     */
    // Default: passable tiles (empty, hints, etc.)
    // Clear forced movement when landing on non-arrow tile
    this.clearForcedDirection();

    // Check for entity collisions first
    if (!isEmpty(entity)) {
      logger.info(
        `[PlayerEntity] Collision with entity at target:`,
        JSON.stringify({
          entity,
          direction
        })
      );
      // Moving into bee/bug/cat - player dies
      if (
        entity === TILE.BEE_HORIZONTAL ||
        entity === TILE.BEE_VERTICAL ||
        entity === TILE.BUG ||
        entity === TILE.CAT
      ) {
        return { type: "die", collisionWith: "entity", who: "self", direction };
      }

      // Water blocks can be pushed
      if (entity === TILE.WATER_BLOCK || entity === TILE.BALL) {
        return { type: "push", direction };
      }

      // Other entities block movement for now
      return { type: "wall" };
    }

    // Check tile interactions
    if (tile === TILE.WALL) {
      return { type: "wall" };
    }

    // Collectibles
    if (tile === TILE.STAR) {
      return { type: "collectStar", direction };
    }

    if (tile === TILE.KEY_GREEN) {
      return { type: "collectKey", direction, keyColor: "green" };
    }
    if (tile === TILE.KEY_RED) {
      return { type: "collectKey", direction, keyColor: "red" };
    }
    if (tile === TILE.KEY_ORANGE) {
      return { type: "collectKey", direction, keyColor: "orange" };
    }
    if (tile === TILE.KEY_BLUE) {
      return { type: "collectKey", direction, keyColor: "blue" };
    }

    if (tile === TILE.FLIPPERS) {
      return { type: "collectFlippers", direction };
    }

    if (tile === TILE.SUCTION_BOOTS) {
      return { type: "collectSuctionBoots", direction };
    }

    // Doors
    if (tile === TILE.DOOR_GREEN) {
      if (context.globalState.hasKey("green")) {
        return { type: "openDoor", direction, doorColor: "green" };
      }
      return { type: "wall" };
    }
    if (tile === TILE.DOOR_RED) {
      if (context.globalState.hasKey("red")) {
        return { type: "openDoor", direction, doorColor: "red" };
      }
      return { type: "wall" };
    }
    if (tile === TILE.DOOR_ORANGE) {
      if (context.globalState.hasKey("orange")) {
        return { type: "openDoor", direction, doorColor: "orange" };
      }
      return { type: "wall" };
    }
    if (tile === TILE.DOOR_BLUE) {
      if (context.globalState.hasKey("blue")) {
        return { type: "openDoor", direction, doorColor: "blue" };
      }
      return { type: "wall" };
    }

    if (tile === TILE.BUTTON_DOOR) {
      return { type: "wall" }; // Door closed
    }

    // Water
    if (tile === TILE.WATER) {
      if (context.globalState.hasFlippers) {
        return { type: "move", direction }; // Can swim
      }
      return { type: "drown", direction }; // Die
    }

    // Exit
    if (tile === TILE.EXIT) {
      if (context.globalState.hasAllStars()) {
        return { type: "win", direction }; // Level complete!
      }
      return { type: "wall" }; // Need all stars first
    }

    // Buttons (step on them)
    if (tile === TILE.BUTTON) {
      const offset = direction.toVector();
      return {
        type: "pressButton",
        direction,
      };
    }

    // Rotation button (rotates all arrows)
    if (tile === TILE.ROTATION_BUTTON) {
      return { type: "triggerRotation", direction };
    }

    // Hint tile (show hint in sidebar)
    if (tile === TILE.HINT) {
      return { type: "showHint", direction };
    }

    // Sand (passable, but will disappear)
    if (tile === TILE.SAND) {
      return { type: "stepOnSand", direction };
    }

    // Bombs (instant death)
    if (tile === TILE.BOMB) {
      return { type: "die", collisionWith: "tile", who: "self", direction };
    }

    // Arrows - enter forced movement mode
    if (tile && isArrowTile(tile) && !context.globalState.hasSuctionBoots) {
      this.setForcedDirection(getArrowDirection(tile));
      return { type: "move", direction };
    }


    // Check if leaving a hint tile
    const currentTile = context.getAt(this.coordinates()).tile;
    if (currentTile === TILE.HINT) {
      return { type: "hideHint", direction };
    } else if (currentTile === TILE.BUTTON) {
      return { type: "releaseButton", direction }
    }

    return { type: "move", direction };
  }

  clone(): PlayerEntity {
    const copy = new PlayerEntity({
      col: this.col,
      row: this.row,
      id: this.id,
    });
    // Don't copy pendingMove - it's transient state cleared after each tick
    copy.sprite = this.sprite;
    copy.hasFlippers = this.hasFlippers;
    copy.hasSuctionBoots = this.hasSuctionBoots;
    copy.forcedDirection = this.forcedDirection;
    copy.timeSinceLastMove = this.timeSinceLastMove;
    return copy;
  }

  toJSON(): Record<string, any> {
    return {
      type: "player",
      id: this.id,
      col: this.col,
      row: this.row,
      pendingMove: this.pendingMove,
      hasFlippers: this.hasFlippers,
      hasSuctionBoots: this.hasSuctionBoots,
      forcedDirection: this.forcedDirection,
      timeSinceLastMove: this.timeSinceLastMove,
    };
  }
}
